import { CurrencyPipe, DecimalPipe } from '@angular/common';
import { TranslateService } from '@ngx-translate/core';
import { CartSizesPipe, MethodsPipe } from '@trp/community/shared/ui';
import {
  CommunityProfileWidgetConfig,
  SINGLE_FAMILY_SOLID_WASTE_VERIFIABLE_PROPERTIES,
} from '@trp/community/interfaces';
import {
  ContainersPipe,
  CurbsideContaminationPipe,
} from '@trp/community/shared/ui';
import { SingleFamilySolidWasteProgram } from '@trp/nationaldb/interfaces';

// hasOrdinance is a tri-state in NDB: 1=true, 0=false, -1=affirmative null.
// (Typed as boolean|null but arrives as an integer at runtime.) -1/null/
// undefined are "unspecified"; only 0/1 are a real answer. Strict checks avoid
// the Number(null)===0 trap.
const ordinanceIsYes = (v: unknown) => v === 1 || v === true;
const ordinanceIsSet = (v: unknown) =>
  ordinanceIsYes(v) || v === 0 || v === false;

// The seven enumerated fields (collection type/who, container type/provider,
// frequency, funding sources, waste-generation disincentives) are rendered by
// `trp-solid-waste-enum-widgets`, which resolves their ids to display values
// asynchronously (DATA-1667). This config only covers the scalar fields.
const singleFamilySolidWastePrograms = (
  translateService: TranslateService,
  decimalPipe: DecimalPipe,
  currencyPipe: CurrencyPipe,
  containersPipe: ContainersPipe,
  curbsideContaminationPipe: CurbsideContaminationPipe,
  cartSizesPipe: CartSizesPipe,
  methodsPipe: MethodsPipe
): CommunityProfileWidgetConfig['singleFamilySolidWastePrograms'] => ({
  section: 'singleFamilySolidWastePrograms',
  title: 'widgets.singleFamilySolidWasteProgram.title',
  maxColumns: 3,
  propertiesToVerify: [...SINGLE_FAMILY_SOLID_WASTE_VERIFIABLE_PROPERTIES],
  widgets: [
    {
      section: 'singleFamilySolidWastePrograms',
      infoType: 'householdsServed',
      type: 'standard',
      locations: ['community-profile', 'map-profile'],
      title: 'widgets.singleFamilySolidWasteProgram.householdsServed',
      icon: 'custom-solid:Home',
      ifInfoAvailable: (info: SingleFamilySolidWasteProgram) =>
        info.householdsServed !== null && info.householdsServed !== undefined,
      availableResolver: (info: SingleFamilySolidWasteProgram) =>
        decimalPipe.transform(info.householdsServed) ?? '',
      unavailableResolver: () =>
        translateService.instant(
          'widgets.singleFamilySolidWasteProgram.notice.noHouseholdsServedInfo'
        ),
    },
    {
      section: 'singleFamilySolidWastePrograms',
      infoType: 'hasOrdinance',
      type: 'standard',
      locations: ['community-profile', 'map-profile'],
      title: 'widgets.singleFamilySolidWasteProgram.hasOrdinance',
      icon: 'global:table-chart',
      ifInfoAvailable: (info: SingleFamilySolidWasteProgram) =>
        ordinanceIsSet(info.hasOrdinance),
      availableResolver: (info: SingleFamilySolidWasteProgram) =>
        translateService.instant(
          ordinanceIsYes(info.hasOrdinance) ? 'common.yes' : 'common.no'
        ),
      unavailableResolver: () =>
        translateService.instant(
          'widgets.singleFamilySolidWasteProgram.notice.noOrdinanceInfo'
        ),
    },
    {
      section: 'singleFamilySolidWastePrograms',
      infoType: 'ordinanceUrl',
      type: 'standard',
      locations: ['community-profile', 'map-profile'],
      availableValueFormat: 'html',
      title: 'widgets.singleFamilySolidWasteProgram.ordinanceUrl',
      icon: 'global:alternate-email',
      ifInfoAvailable: (info: SingleFamilySolidWasteProgram) =>
        ordinanceIsYes(info.hasOrdinance) && !!info.ordinanceUrl,
      availableResolver: (info: SingleFamilySolidWasteProgram) =>
        `<a href="${info.ordinanceUrl}" class="link" target="_blank">${info.ordinanceUrl}</a>`,
      unavailableResolver: () =>
        translateService.instant(
          'widgets.singleFamilySolidWasteProgram.notice.noOrdinanceUrlInfo'
        ),
    },
    {
      section: 'singleFamilySolidWastePrograms',
      infoType: 'mswTipFee',
      type: 'standard',
      locations: ['community-profile'],
      availableValueFormat: 'html',
      title: 'widgets.singleFamilySolidWasteProgram.mswTipFee',
      icon: 'custom-solid:MedianIncom',
      ifInfoAvailable: (info: SingleFamilySolidWasteProgram) =>
        info.mswTipFee !== null && info.mswTipFee !== undefined,
      availableResolver: (info: SingleFamilySolidWasteProgram) =>
        currencyPipe.transform(info.mswTipFee, '$', 'symbol', '1.2-2'),
      unavailableResolver: () =>
        translateService.instant(
          'widgets.singleFamilySolidWasteProgram.notice.noMswTipFeeInfo'
        ),
    },
    {
      section: 'singleFamilySolidWastePrograms',
      infoType: 'websiteUrl',
      type: 'standard',
      locations: ['community-profile', 'map-profile'],
      title: 'widgets.singleFamilySolidWasteProgram.websiteUrl',
      icon: 'global:alternate-email',
      ifInfoAvailable: (info: SingleFamilySolidWasteProgram) =>
        !!info.websiteUrl,
      availableValueFormat: 'html',
      availableResolver: (info: SingleFamilySolidWasteProgram) =>
        `<a href="${info.websiteUrl}" class="link" target="_blank">${info.websiteUrl}</a>`,
      unavailableResolver: () =>
        translateService.instant(
          'widgets.singleFamilySolidWasteProgram.notice.noWebsiteUrlInfo'
        ),
    },
  ],
});

export default singleFamilySolidWastePrograms;
